// ============================================================
// diagnostic-engine.js
// Moteur de calcul du diagnostic cutané Kosmorebi
//
// Piliers :
//   B — Barrière & confort
//   R — Renouvellement & éclat
//   S — Séborégulation
//   O — Équilibre oxydatif
//   F — Fermeté
// ============================================================


function runDiagnostic(answers) {
  if (!answers || typeof answers !== 'object') {
    throw new Error('[diagnostic-engine] answers est invalide.')
  }
  const scores          = calculerScores(answers)
  const normalises      = normaliserScores(scores)
  const profils         = detecterProfils(answers, normalises)
  const recommandations = construireRecommandations(answers, normalises, profils)
  const notes           = construireNotes(recommandations, profils)
  const articles = recommanderArticles(profils, answers)
  return { scores: { bruts: scores, normalises }, profils, recommandations, notes, articles }
}


// ------------------------------------------------------------
// 1. SCORES BRUTS
// ------------------------------------------------------------

function estQuestionActive(question, answers) {
  if (!question.condition) return true
  const { question: qId, answer } = question.condition
  const reponses = answers[qId]
  if (!reponses) return false
  const reponsesArray = Array.isArray(reponses) ? reponses : [reponses]
  const answersArray  = Array.isArray(answer)   ? answer   : [answer]
  return answersArray.some(a => reponsesArray.includes(a))
}

function calculerScores(answers) {
  const scores = { B: 0, R: 0, S: 0, O: 0, F: 0 }

  // Q1 à Q11 uniquement (scoring physiologique)
  const questionsDiag = QUESTIONS.filter(q => {
    const num = parseInt(q.id.replace('q', ''), 10)
    return num >= 1 && num <= 11
  })

  questionsDiag.forEach(question => {
    if (!estQuestionActive(question, answers)) return
    const reponses = answers[question.id]
    if (!reponses) return
    const reponsesArray = Array.isArray(reponses) ? reponses : [reponses]

    reponsesArray.forEach(reponseId => {
      const option = question.options.find(o => o.id === reponseId)
      if (!option || !option.scores) return
      scores.B += option.scores.B || 0
      scores.R += option.scores.R || 0
      scores.S += option.scores.S || 0
      scores.O += option.scores.O || 0
      scores.F += option.scores.F || 0
    })
  })

  return scores
}


// ------------------------------------------------------------
// 2. MAXIMA AUTOMATIQUES
// Simule le pire cas pour chaque pilier
// en tenant compte des conditionnelles
// ------------------------------------------------------------

function calculerMaxima() {
  const max = { B: 0, R: 0, S: 0, O: 0, F: 0 }

  const questionsDiag = QUESTIONS.filter(q => {
    const num = parseInt(q.id.replace('q', ''), 10)
    return num >= 1 && num <= 11
  })

  questionsDiag.forEach(question => {
    if (question.type === 'radio') {
      // Pour les questions à choix unique : prendre le max par pilier
      const maxOption = { B: 0, R: 0, S: 0, O: 0, F: 0 }
      question.options.forEach(option => {
        if (option.exclusive || !option.scores) return
        Object.keys(maxOption).forEach(pilier => {
          maxOption[pilier] = Math.max(maxOption[pilier], option.scores[pilier] || 0)
        })
      })
      Object.keys(max).forEach(pilier => {
        max[pilier] += maxOption[pilier]
      })
    } else {
      // Pour les questions à choix multiple : additionner toutes les options
      question.options.forEach(option => {
        if (option.exclusive || !option.scores) return
        Object.keys(max).forEach(pilier => {
          max[pilier] += option.scores[pilier] || 0
        })
      })
    }
  })

  return max
}

const SCORES_MAX = calculerMaxima()


// ------------------------------------------------------------
// 3. NORMALISATION
// Formule : indice = 9.5 - 7.5 × (score_brut / max)
// Borné entre 2 et 9.5, arrondi à 1 décimale
// ------------------------------------------------------------

function normaliserScores(scores) {
  const normalises = {}
  Object.keys(scores).forEach(pilier => {
    const max = SCORES_MAX[pilier]
    if (!max) {
      normalises[pilier] = 9.5
      return
    }
    const indice = 9.5 - 7.5 * (scores[pilier] / max)
    normalises[pilier] = Math.round(Math.min(9.5, Math.max(2, indice)) * 10) / 10
  })
  return normalises
}


// ------------------------------------------------------------
// 4. DÉTECTION DES PROFILS
// Q2 à Q16 — règles métier uniquement
// ------------------------------------------------------------

function detecterProfils(answers, normalises) {
  const piliers = Object.keys(normalises)
  const equilibre = piliers.every(p => normalises[p] >= 9)

  // Pilier dominant = indice le plus bas (fragilité la plus forte)
  let dominant = null
  if (!equilibre) {
    let minScore = Infinity
    piliers.forEach(p => {
     if (normalises[p] < minScore) { minScore = normalises[p]; dominant = p }
    })
  }

  const piliersSignificatifs = piliers
  .filter(p => normalises[p] < SEUILS_NIVEAUX.eleve)   // prioritaire + fortement marqué
  .sort((a, b) => normalises[a] - normalises[b])

const piliersMarques = piliers
  .filter(p => normalises[p] >= SEUILS_NIVEAUX.eleve && normalises[p] < SEUILS_NIVEAUX.marque)
  .sort((a, b) => normalises[a] - normalises[b])

const piliersModeres = piliers
  .filter(p => normalises[p] >= SEUILS_NIVEAUX.marque)
  .sort((a, b) => normalises[a] - normalises[b])

  // Piliers dominants pour l'intro routine (écart <= 1.5 depuis le score min)
const scoreMin = Math.min(...Object.values(normalises))
const piliersDominants = piliers
  .filter(p => normalises[p] - scoreMin <= 1.5)
  .sort((a, b) => normalises[a] - normalises[b])



  // Q4 – Brillance
  const poresDilates     = (answers.q4 || []).includes('q4_pores')
  const brillanceSeulement = (answers.q4 || []).length > 0 && !poresDilates

  // Q5 – Imperfections
  const reponsesQ5          = answers.q5 || []
  const aDesImperfections   = reponsesQ5.length > 0
  const imperfInflammatoires = reponsesQ5.includes('q5_inflammatoires')
  const imperfEtendues       = reponsesQ5.includes('q5_etendues')

  // Règle combinée Q4+Q5
  const peelingPrioritaire = poresDilates || (brillanceSeulement && aDesImperfections) || imperfInflammatoires || imperfEtendues

  // Q7-Q8 – Taches pigmentaires
  const reponsesQ7 = answers.q7 || []
  const reponsesQ8 = answers.q8 || []
  const tachesRecentesOuRapides = reponsesQ8.includes('q8_rapide')

  // Q9 – Mode de vie
  const reponsesQ9       = answers.q9 || []
  const expositionSolaire = reponsesQ9.includes('q9_soleil')
  const sommeilInsuffisant = reponsesQ9.includes('q9_sommeil')
  const stressFrequent     = reponsesQ9.includes('q9_stress')


  // Q10 – Rides et fermeté
  const reponsesQ10      = answers.q10 || []
  const ridulesLines     = reponsesQ10.includes('q10_ridules')
  const ridesExpression = reponsesQ10.includes('q10_localisees')
  const ridesMarquees    = reponsesQ10.includes('q10_marquees')

  // Q12 — Maquillage
  const frequenceMaquillage = answers.q12 || 'jamais'
  const seMaquille = frequenceMaquillage !== 'jamais'

  // Q13 — Nettoyage avec maquillage / Q14 — sans maquillage
  const nettoyageSoir = seMaquille
    ? (answers.q13 || null)
    : (answers.q14 || null)
  
  const nettoyageInsuffisant = nettoyageSoir === 'q13_pas_systematique'

  const nettoyageSoirInsuffisant = ['q14_pas_nettoyage', 'q14_eau'].includes(nettoyageSoir)

  // Q15 — Préférence nettoyage
  const preferenceNettoyage = answers.q15 || null
  const sansRincage = preferenceNettoyage === 'q15_sans_rincage'
  const avecRincage = preferenceNettoyage === 'q15_avec_rincage'

  // Waterproof (Q12 dernière option)
  const waterproof = frequenceMaquillage === 'q12_waterproof'
  const waterproofSansRincage = waterproof && sansRincage

  // Q16 — Mode de vie
  const modeDeVie   = answers.q16 || 'aucun'
  const voyage      = modeDeVie === 'q16_voyage'
  const sport       = modeDeVie === 'q16_sport'
  const manqueTemps = modeDeVie === 'q16_manque_temps'

  const prendTemps  = modeDeVie === 'prend_temps'

  // Peau réactive aux cosmétiques (Q2)
  const reponsesQ2 = answers.q2 || []
  const reponsesQ2Array = Array.isArray(reponsesQ2) ? reponsesQ2 : [reponsesQ2]
  const reactiveCosmetiques = reponsesQ2Array.includes('q2_cosmetiques')
  const sensibiliteMarquee  = reponsesQ2Array.filter(r => r !== 'q2_aucune').length >= 2
  const maquillageZonesSèches = (answers.q3 || []).includes('q3_maquillage')


  return {
    peau: { equilibre, reactiveCosmetiques, sensibiliteMarquee, pilierDominant: dominant, piliersSignificatifs, piliersMarques, piliersModeres, piliersDominants },
    poresDilates, brillanceSeulement, aDesImperfections, imperfInflammatoires, imperfEtendues, peelingPrioritaire,
    nettoyage: { waterproof, sansRincage, avecRincage, nettoyageSoir, nettoyageInsuffisant, nettoyageSoirInsuffisant, waterproofSansRincage, pilierDominant: dominant },
    maquillage:         { frequence: frequenceMaquillage, waterproof, seMaquille },
    modeDVie:           { voyage, sport, manqueTemps, prendTemps },
    reactiveCosmetiques,
    sensibiliteMarquee,
    maquillageZonesSèches,
    tachesRecentesOuRapides,
    expositionSolaire,
    sommeilInsuffisant,
    stressFrequent,
    ridulesLines, 
    ridesExpression, 
    ridesMarquees,
    pilierDominant:     dominant,
    piliersSignificatifs, piliersMarques, piliersModeres, piliersDominants,
    equilibre
  }
}

function recommanderArticles(profils, answers) {
  const piliersCibles = [
    profils.pilierDominant,
    ...profils.piliersSignificatifs
  ].filter(Boolean)

  // Articles par pilier
  const articlesPilier = ARTICLES_BLOG.filter(a =>
    !a.conditionnel && (
      a.piliers.length > 1 ||
      a.piliers.some(p => piliersCibles.includes(p))
    )
  ).slice(0, 3)

  // Articles conditionnels
  const conditions = {
    peauSensible: profils.reactiveCosmetiques || profils.sensibiliteMarquee,
    stress:       (answers.q9 || []).includes('q9_stress'),
    tiraillement: ['q14_eau', 'q14_pas_nettoyage'].includes(answers.q14) || profils.nettoyage?.nettoyageSoirInsuffisant,
    soleil:       profils.expositionSolaire,
    secheresse:   (answers.q3 || []).includes('q3_zones_seches')
  }

  const articlesConditionnels = ARTICLES_BLOG.filter(a =>
    a.conditionnel && conditions[a.conditionnel]
  )

  return { pilier: articlesPilier, conditionnel: articlesConditionnels }
}

// ------------------------------------------------------------
// 5. RECOMMANDATIONS
// ------------------------------------------------------------

function construireRecommandations(answers, normalises, profils) {
  const recommandations = { nettoyage: [], soinsVisage: [], soinsHebdomadaires: [], corps: [] }

  if (profils.equilibre) {
    recommandations.soinsVisage.push(construireRecommandationProduit('peps-creme', 'equilibre', answers))
    recommandations.nettoyage.push(...recommanderNettoyant(answers, profils))
    recommandations.corps.push(...recommanderCorps(answers))
    return dedoublonner(recommandations)
  }

  recommandations.soinsVisage.push(...recommanderSoinsVisage(normalises, profils, answers))
  recommandations.soinsHebdomadaires.push(...recommanderSoinsHebdomadaires(normalises, profils, answers))
  recommandations.nettoyage.push(...recommanderNettoyant(answers, profils))
  recommandations.corps.push(...recommanderCorps(answers))
  
  const avecPeps = recommandations.soinsVisage.some(p => p.id === 'peps-creme')
  const cocoon   = recommandations.soinsVisage.find(p => p.id === 'cocoon-creme')
  if (cocoon && !avecPeps) {
    cocoon.routine.moment = 'matin_et_soir'
  }
  
  return dedoublonner(recommandations)
  }

  


function recommanderSoinsVisage(normalises, profils, answers) {
  const soins = []

  if (profils.reactiveCosmetiques) {
    soins.push(construireRecommandationProduit('cocoon-creme', 'barriere', answers))
    return soins
  }
  if (profils.sensibiliteMarquee) {
  soins.push(construireRecommandationProduit('cocoon-creme', 'barriere_renforcee', answers))
  return soins
  }
  if (profils.maquillageZonesSèches) {
  soins.unshift(construireRecommandationProduit('cocoon-creme', 'preparateur_maquillage', answers))
  }

  const besoinB = normalises.B < SEUILS_NIVEAUX.marque
  const besoinR = normalises.R < SEUILS_NIVEAUX.marque
  const besoinF = normalises.F < SEUILS_NIVEAUX.marque
  const besoinO = normalises.O < SEUILS_NIVEAUX.marque
  const besoinS = normalises.S < SEUILS_NIVEAUX.marque

  const modeDeVie = profils.modeDVie

  if (!modeDeVie.manqueTemps && besoinB && (besoinR || besoinF)) {
    soins.push(construireRecommandationProduit('peps-creme',   'eclat_fermete', answers))
    soins.push(construireRecommandationProduit('cocoon-creme', 'barriere',      answers))
    return soins
  }

  if (besoinB && profils.pilierDominant === 'B') {
    soins.push(construireRecommandationProduit('cocoon-creme', 'barriere', answers))
  } else {

    const contexte = (() => {
      if ((profils.pilierDominant === 'F' || profils.pilierDominant === 'R') && (besoinF || besoinR)) return 'eclat_fermete'
      if (profils.pilierDominant === 'O' && besoinO) return 'oxydatif'
      if (profils.pilierDominant === 'S' && besoinS) return 'seborregulatrice'
      return 'prevention'
    })()
    soins.push(construireRecommandationProduit('peps-creme', contexte, answers))
  }



  return soins
}

function recommanderSoinsHebdomadaires(normalises, profils, answers) {
  const soins = []

  if (profils.reactiveCosmetiques)  return soins
  if (profils.modeDVie.manqueTemps) return soins

  let peelingAjout = false

  if (profils.peelingPrioritaire) {
    soins.unshift(construireRecommandationProduit('pure-peeling', 'peeling_prioritaire', answers))
    peelingAjout = true
  } else if (profils.aDesImperfections) {
    soins.push(construireRecommandationProduit('pure-peeling', 'peeling_complementaire', answers))
    peelingAjout = true
  }

  if (!peelingAjout && normalises.S < SEUILS_NIVEAUX.faible) {
    soins.push(construireRecommandationProduit('pure-peeling', 'equilibre', answers))
  }

  const pepsMasqueDeclencheur = 
  answers.q6?.includes('q6_terne') || 
  answers.q6?.includes('q6_grain');

  if (normalises.R < SEUILS_NIVEAUX.faible || pepsMasqueDeclencheur) {
    soins.push(construireRecommandationProduit('peps-masque', 'eclat', answers))
  }
  if (profils.stressFrequent && !soins.some(p => p.id === 'peps-masque')) {
    soins.push(construireRecommandationProduit('peps-masque', 'stress', answers))
  }

  return soins
}

function recommanderNettoyant(answers, profils) {
  const nettoyants  = []
  const sansRincage = profils.nettoyage.sansRincage
  const avecRincage = profils.nettoyage.avecRincage
  const waterproof  = profils.nettoyage.waterproof
  const rm01        = profils.reactiveCosmetiques
  const { voyage, sport } = profils.modeDVie
  const pepsMoussePiliers = ['O', 'F', 'R']
  const mousse = (!rm01 && pepsMoussePiliers.includes(profils.pilierDominant))
    ? 'peps-mousse'
    : 'velvet-mousse'

  const oleogel_force = waterproof || voyage || profils.imperfInflammatoires || avecRincage

  if (sansRincage || !oleogel_force) {
    nettoyants.push(construireRecommandationProduit(mousse, 'nettoyage', answers))
    if (oleogel_force) {
      nettoyants.push(construireRecommandationProduit('pure-oleogel', 'nettoyage', answers))
    }
  } else {
    nettoyants.push(construireRecommandationProduit('pure-oleogel', 'nettoyage', answers))
    if (sport || sansRincage) {
      nettoyants.push(construireRecommandationProduit(mousse, 'nettoyage', answers))
    }
  }

  return nettoyants
}


function recommanderCorps(answers) {
  const reponsesQ = answers.q17 || []
  const besoin = reponsesQ.some(r => r !== 'aucun')
  if (!besoin) return []
  return [construireRecommandationProduit('cocoon-baume-corps', 'soin_corps', answers)]
}


function dedoublonner(recommandations) {
  Object.keys(recommandations).forEach(cat => {
    const vus = {}
    recommandations[cat] = recommandations[cat]
      .filter(Boolean)
      .filter(produit => {
        if (vus[produit.id]) return false
        vus[produit.id] = true
        return true
      })
  })
  return recommandations
}


// ------------------------------------------------------------
// 6. CONSTRUCTION PRODUIT + ROUTINE
// ------------------------------------------------------------

function construireRecommandationProduit(produitId, contexte, answers) {
  const produit = PRODUITS.find(p => p.id === produitId)
  if (!produit) {
    console.warn(`[diagnostic-engine] Produit introuvable : ${produitId}`)
    return null
  }
  return {
    id:       produit.id,
    handle:   produit.handle,
    title:    produit.title,
    category: produit.category,
    image:    produit.image,
    contexte,
    routine:  construireRoutine(produit.id, answers),
    reason:   '',
    text:     ''
  }
}

function construireRoutine(produitId, answers) {
  const momentsPreferentiels = {
    'cocoon-creme':       'soir',
    'peps-creme':         'matin',
    'pure-peeling':       'soir',
    'peps-masque':        'soir',
    'velvet-mousse':      'matin_et_soir',
    'peps-mousse':        'matin_et_soir',
    'pure-oleogel':       'soir',
    'cocoon-baume-corps': 'soir'
  }
  const frequences = {
    'pure-peeling': '1 à 2 fois par semaine',
    'peps-masque':  '1 fois par semaine'
  }
  const momentProduit = momentsPreferentiels[produitId] || 'matin_et_soir'
  const routine = { moment: momentProduit, frequence: frequences[produitId] || null, note: null }
  return routine
}


// ------------------------------------------------------------
// 7. NOTES
// ------------------------------------------------------------

function construireNotes(recommandations, profils) {
  const notes      = []
  const soinsHebdo = recommandations.soinsHebdomadaires.filter(Boolean).map(p => p.id)

  // --- AVANT LA ROUTINE ---

  if (profils.nettoyage.nettoyageInsuffisant) {
    notes.push({
      titre: 'Conseil soin',
      texte: 'Le nettoyage du soir permet d\'éliminer le sébum, les particules de pollution, ' +
        'les résidus de maquillage et de protection solaire accumulés au cours de la journée. ' +
        'Réalisé chaque soir, il contribue à préserver l\'équilibre de la peau et s\'intègre ' +
        'naturellement dans une routine de soin efficace.'
    })
  }

  if (profils.nettoyage.nettoyageSoirInsuffisant) {
    notes.push({
      titre: 'Conseil soin',
      texte: 'Même sans maquillage, la peau accumule naturellement du sébum, des particules de ' +
        'pollution et d\'autres impuretés au cours de la journée. Un nettoyage doux chaque soir ' +
        'permet de les éliminer et constitue une étape importante pour préserver l\'équilibre de la peau.'
    })
  }

  if (profils.pilierDominant === 'B' && !profils.equilibre && !profils.reactiveCosmetiques) {
    notes.push({
      titre: 'Conseil nettoyage',
      texte: 'Lorsqu\'une peau présente des signes d\'inconfort, un nettoyage trop agressif peut ' +
        'accentuer les tiraillements et fragiliser davantage la fonction barrière. Privilégier un ' +
        'nettoyage doux, sans frottements inutiles, permet de préserver cet équilibre au quotidien.'
    })
    notes.push({
      titre: 'Conseil hydratation',
      texte: 'Une peau confortable ne dépend pas uniquement de la quantité d\'eau qu\'elle contient, ' +
        'mais aussi de sa capacité à la retenir. L\'application quotidienne d\'une crème adaptée ' +
        'contribue à soutenir la fonction barrière et à limiter les sensations de tiraillement ' +
        'ou d\'inconfort.'
    })
  }

  if (profils.modeDVie.sport) {
    notes.push({
      titre: 'Conseil pratique',
      position: 'apres',
      texte: 'Après une activité sportive, un nettoyage doux aide à éliminer la transpiration, ' +
        'le sébum et les impuretés accumulés pendant l\'effort. Les mousses recommandées dans ' +
        'votre routine peuvent être utilisées avec ou sans rinçage, pour un geste simple et ' +
        'rapide, même en déplacement.'
    })
  }

  if (profils.maquillageZonesSèches) {
    notes.push({
      titre: 'Conseil maquillage',
      texte: 'Vous nous avez indiqué que votre maquillage a tendance à marquer certaines zones sèches. ' +
        'Évitez de compenser en multipliant les couches : cela accentue souvent le phénomène.\n\n' +
        'Une peau bien préparée fait souvent toute la différence. En aidant à préserver la fonction barrière ' +
        'et à limiter la déshydratation, la crème recommandée dans cette routine contribue à rendre la surface ' +
        'de la peau plus régulière. Le maquillage s\'applique alors plus facilement et son rendu en est plus uniforme.'
    })
  }

  if (profils.poresDilates) {
  notes.push({
    titre: 'Conseil soin',
    texte: 'Les pores font partie de la structure normale de la peau. Ils peuvent paraître plus marqués ' +
      'lorsqu\'ils accumulent du sébum et des cellules mortes, ce qui donne parfois l\'impression ' +
      'd\'un grain de peau irrégulier.\n\n' +
      'Des soins favorisant le renouvellement de la couche superficielle de la peau contribuent ' +
      'progressivement à affiner l\'aspect des pores et à homogénéiser le grain de peau. ' +
      'Les résultats s\'observent généralement au fil des utilisations.'
  })
}
    if (profils.imperfInflammatoires) {
    notes.push({
      titre: 'Conseil nettoyage',
      texte: 'Lorsqu\'une peau présente des imperfections inflammatoires, il peut être tentant ' +
        'd\'utiliser des nettoyants très décapants. Pourtant, une irritation répétée risque ' +
        'd\'entretenir l\'inflammation et de fragiliser la barrière cutanée. Privilégier un ' +
        'nettoyage doux permet de préserver cet équilibre tout en préparant la peau à recevoir ' +
        'les soins ciblés de votre routine.'
    })
  }


    if (profils.peelingPrioritaire && profils.aDesImperfections) {
    notes.push({
      titre: 'Conseil soin',
      texte: 'Lorsque la peau a tendance à briller tout en présentant des imperfections, l\'acide ' +
        'salicylique est particulièrement adapté : il aide à éliminer progressivement les cellules ' +
        'mortes, à maintenir les pores dégagés et à réduire l\'aspect des imperfections.'
    })
  }

  if (profils.reactiveCosmetiques) {
    notes.push({
      titre: 'Conseil soin',
      texte: 'Lorsqu\'une peau réagit facilement aux cosmétiques, ajouter de nombreux actifs n\'est pas ' +
        'toujours la meilleure stratégie. Nous avons donc privilégié une routine simple, centrée sur ' +
        'la préservation de la fonction barrière afin de limiter les sensations d\'inconfort. ' +
        'Une fois la peau mieux équilibrée, des soins plus ciblés pourront être introduits progressivement.'
    })
  }

  if (
    profils.pilierDominant === 'S' &&
    recommandations.soinsVisage.some(p => p.id === 'peps-creme') &&
    !profils.equilibre &&
    !profils.reactiveCosmetiques
  ) {
    notes.push({
      titre: 'Conseil hydratation',
      texte: 'Même lorsqu\'une peau présente quelques imperfections, une hydratation quotidienne ' +
        'reste importante. Une formule légère permet de préserver le confort cutané tout en ' +
        'accompagnant l\'équilibre de la peau, sans alourdir la routine. L\'association d\'actifs ' +
        'antioxydants et d\'ingrédients aux propriétés légèrement astringentes complète cette ' +
        'approche en accompagnant naturellement les besoins des peaux mixtes à tendance grasse.'
    })
  }

  if (profils.tachesRecentesOuRapides) {
    notes.push({
      titre: 'Bon à savoir',
      texte: 'Les taches pigmentaires peuvent avoir des origines très variées et sont, dans la grande ' +
        'majorité des cas, bénignes. En revanche, si vous avez remarqué l\'apparition récente d\'une ' +
        'tache, une évolution rapide de son aspect (taille, forme, couleur...) ou si elle vous paraît ' +
        'inhabituelle, il est préférable de la faire examiner par un professionnel de santé. Seul un ' +
        'examen clinique permet d\'en déterminer la nature.'
    })
  }

    if (profils.piliersSignificatifs.includes('O') || profils.piliersMarques.includes('O')) {
    notes.push({
      titre: 'Conseil soin',
      texte: 'Les antioxydants contribuent à limiter les réactions d\'oxydation provoquées par les ' +
        'radicaux libres. Associer plusieurs antioxydants aux modes d\'action complémentaires, comme ' +
        'les vitamines C et E ou certains caroténoïdes, permet d\'accompagner les systèmes de défense ' +
        'naturellement présents dans la peau.'
    })
    notes.push({
      titre: 'Conseil pratique',
      texte: 'L\'alimentation participe elle aussi aux défenses antioxydantes de l\'organisme. ' +
        'Une alimentation variée, riche en fruits et légumes, apporte notamment des vitamines, ' +
        'des caroténoïdes et de nombreux composés végétaux qui contribuent à ces systèmes de protection.'
    })
    notes.push({
      titre: 'Conseil pratique',
      texte: 'L\'activité physique illustre bien la capacité d\'adaptation de l\'organisme : ' +
        'l\'exercice augmente transitoirement la production d\'espèces réactives de l\'oxygène, ' +
        'mais une pratique régulière stimule également les systèmes de défense antioxydants. ' +
        'La régularité compte donc davantage que l\'intensité ponctuelle : une activité modérée ' +
        'pratiquée régulièrement est généralement plus favorable à ces mécanismes d\'adaptation ' +
        'que des efforts intenses très occasionnels.'
    })
  }

  if (profils.expositionSolaire) {
    notes.push({
      titre: 'Conseil pratique',
      texte: 'Le soleil n\'est pas un ennemi, mais les expositions répétées ou excessives augmentent ' +
        'le stress oxydatif auquel la peau est exposée. Lorsque l\'exposition est importante, ' +
        'privilégiez autant que possible les vêtements, le chapeau et l\'ombre. Une protection ' +
        'solaire adaptée vient ensuite compléter ces mesures lorsque l\'exposition ne peut être évitée.'
    })
  }

  if (profils.sommeilInsuffisant) {
    notes.push({
      titre: 'Conseil pratique',
      texte: 'Le sommeil participe aux mécanismes de récupération et de régulation de l\'organisme. ' +
        'Lorsqu\'il est régulièrement insuffisant ou perturbé, cet équilibre peut être moins favorable ' +
        'face au stress oxydatif.\n\n' +
        'Lorsque cela est possible, essayez de conserver des horaires relativement réguliers et de ' +
        'créer une transition avant le coucher. Limiter les écrans pendant l\'heure qui précède le ' +
        'sommeil et les remplacer par une activité calme, comme la lecture ou l\'écoute de musique, ' +
        'peut aider à préparer progressivement l\'organisme au repos. Une chambre plutôt fraîche, ' +
        'autour de 17 à 19 °C, favorise également les conditions propices à l\'endormissement. ' +
        'Une boisson chaude non caféinée peut enfin trouver sa place dans ce rituel.'
    })
  }

  if (profils.stressFrequent) {
    notes.push({
      titre: 'Conseil pratique',
      texte: 'Le stress n\'est pas toujours un facteur sur lequel il est facile d\'agir directement. ' +
        'S\'accorder régulièrement quelques minutes de pause peut néanmoins permettre de ralentir ' +
        'le rythme. Le temps de pose d\'un masque peut simplement devenir l\'un de ces moments.'
    })
  }

  if (profils.ridulesLines) {
    notes.push({
      titre: 'Bon à savoir',
      texte: 'Les ridules fines ne sont pas toujours permanentes. Elles peuvent paraître plus marquées ' +
        'lorsque la peau est déshydratée ou que sa fonction barrière est fragilisée. Une routine adaptée ' +
        'aide à préserver ces deux éléments, ce qui contribue souvent à lisser visuellement la surface de la peau.'
    })
  }

  if (profils.ridesExpression) {
    notes.push({
      titre: 'Bon à savoir',
      texte: 'Les rides d\'expression sont liées aux mouvements répétés des muscles du visage au fil ' +
        'des années. Elles font partie de l\'expressivité de chacun et participent à l\'identité du ' +
        'visage. Un automassage doux lors de l\'application du soin peut compléter votre routine en ' +
        'favorisant la détente des traits et en transformant ce moment en véritable geste de bien-être.'
    })
  }

    if (profils.piliersSignificatifs.includes('F') || profils.piliersMarques.includes('F')) {
    notes.push({
      titre: 'Conseil soin',
      texte: 'La vitamine C joue un rôle important dans la synthèse du collagène, l\'une des ' +
        'protéines qui participent à la structure et à la fermeté de la peau. Son activité ' +
        'antioxydante contribue également à protéger les structures cutanées du stress oxydatif. ' +
        'Intégrée régulièrement à une routine de soin, elle constitue ainsi un actif particulièrement ' +
        'intéressant pour accompagner le maintien de la fermeté cutanée.'
    })
  }

    if (profils.ridesMarquees) {
    notes.push({
      titre: 'Bon à savoir',
      texte: 'La fermeté de la peau dépend notamment de l\'organisation et de la qualité de sa ' +
        'matrice extracellulaire. Cet équilibre évolue progressivement au cours du temps et peut ' +
        'être influencé par de nombreux facteurs. Des soins adaptés peuvent accompagner la peau ' +
        'en soutenant les mécanismes qui participent au maintien de sa qualité et de sa tonicité.'
    })
  }



  // --- APRÈS LA ROUTINE ---

  if (soinsHebdo.includes('pure-peeling') && soinsHebdo.includes('peps-masque')) {
    notes.push({
      titre: 'Conseil soin',
      position: 'apres',
      texte: 'Le Peeling PURE et le Masque PEP\'S sont deux soins hebdomadaires complémentaires. ' +
        'Nous vous conseillons de les utiliser en alternance : le Peeling une semaine, puis le Masque ' +
        'la suivante. Cette alternance permet à la peau de bénéficier pleinement des effets de chaque ' +
        'soin, sans la solliciter davantage que nécessaire.'
    })
  }

  if (profils.nettoyage.waterproof) {
    notes.push({
      titre: 'Conseil maquillage',
      position: 'apres',
      texte: 'Le mascara waterproof adhère fortement aux cils et peut être difficile à retirer ' +
        'complètement. L\'Oléogel PURE permet de le démaquiller efficacement tout en limitant ' +
        'les frottements sur les cils et le contour des yeux.'
    })
  }

  if (profils.nettoyage.waterproof && profils.nettoyage.sansRincage) {
    notes.push({
      titre: 'Conseil pratique',
      position: 'apres',
      texte: 'Vous préférez les soins sans rinçage : nous avons tenu compte de cette préférence ' +
        'dans la routine qui vous est proposée. Si vous utilisez un mascara waterproof, l\'Oléogel ' +
        'PURE permet de le retirer efficacement tout en limitant les frottements sur les cils et ' +
        'le contour des yeux.'
    })
  }

  if (profils.modeDVie.voyage) {
    notes.push({
      titre: 'Conseil pratique',
      position: 'apres',
      texte: 'Si vous voyagez régulièrement, l\'Oléogel PURE est particulièrement pratique. ' +
        'Il permet de retirer efficacement le maquillage et la protection solaire, sans coton ' +
        'ni lingettes. Après le massage, un simple rinçage à l\'eau suffit.'
    })
  }

  return notes
}
